import { useRef, forwardRef, useState, useEffect } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import clsx from 'clsx'
import BigNumber from 'bignumber.js'
import OrderBookQuote from '@/components/trade/OrderBookQuote'
import LastPrice from '@/components/trade/LastPrice'
import IconChevronDown from '@/components/icons/IconChevronDown'
import { marketMapState } from '@/recoil/Market'
import { orderBookState } from '@/recoil/OrderBook'
import useOnClickOutside from '@/hooks/useOnClickOutside'

import './OrderBook.css'

const ORDER_BOOK_GROUPING_LIST = 5
const ORDER_BOOK_ROW = 10

function getGroupingPrice(price, grouping, roundMode) {
  const dp = BigNumber(grouping).dp()
  return dp === 0
    ? BigNumber(price)
        .div(grouping)
        .dp(dp, roundMode)
        .times(grouping)
        .toFixed(dp)
    : BigNumber(price).toFixed(dp, roundMode)
}

function generateQuoteList({
  symbol,
  side,
  quoteList,
  orderBookGrouping,
  isDefaultGrouping,
  sizeFormat
}) {
  const isAsk = side === 'ask'
  const roundMode = isAsk ? BigNumber.ROUND_UP : BigNumber.ROUND_DOWN
  const rawlist = quoteList.slice(0, ORDER_BOOK_ROW)
  const maxQuoteSize = rawlist[rawlist.length - 1]?.total || 0
  const quotePriceFormat = BigNumber(orderBookGrouping).dp()

  let groupingSize = 0
  return rawlist.reduce((list, { price, size, total }, index) => {
    const groupingPrice = getGroupingPrice(price, orderBookGrouping, roundMode)
    groupingSize = BigNumber(groupingSize).plus(size).toNumber()
    const key = `${symbol}-${side}-${groupingPrice}`

    const isLastQuote = index === rawlist.length - 1
    const nextQuotePrice = isLastQuote ? 0 : rawlist[index + 1].price
    const nextQuoteGroupingPrice = getGroupingPrice(
      nextQuotePrice,
      orderBookGrouping,
      roundMode
    )
    const compareFn = isAsk ? 'lt' : 'gt'

    const renderQuote =
      isDefaultGrouping ||
      isLastQuote ||
      BigNumber(price).mod(orderBookGrouping).eq(0) || // Price fit grouping
      BigNumber(groupingPrice)[compareFn](nextQuoteGroupingPrice) // Can't be group with next quote

    if (renderQuote) {
      list.push(
        <OrderBookQuote
          key={key}
          side={side}
          price={groupingPrice}
          size={groupingSize}
          total={total}
          maxQuoteSize={maxQuoteSize}
          priceFormat={quotePriceFormat}
          sizeFormat={sizeFormat}
          isAsk={isAsk}
        />
      )
      groupingSize = 0
    }

    return list
  }, [])
}

export default forwardRef(function OrderBook({ children, ...props }, ref) {
  props = {
    ref,
    ...props,
    className: `${props.className} card noselect order-book`
  }

  const [marketMap] = useRecoilState(marketMapState)
  const orderBook = useRecoilValue(orderBookState)
  const marketInfo = marketMap[orderBook.symbol]

  const [orderBookGrouping, setOrderBookGrouping] = useState(false)
  const [orderBookGroupingListActive, setOrderBookGroupingListActive] =
    useState(false)
  const orderBookGroupingListRef = useRef()
  useOnClickOutside(orderBookGroupingListRef, () =>
    setOrderBookGroupingListActive(false)
  )

  useEffect(() => {
    marketInfo &&
      setOrderBookGrouping(BigNumber(marketInfo.minQuoteTick).toFixed())
  }, [orderBook.symbol, marketInfo])

  const OrderBookGroupingList = []
  for (let i = 0; marketInfo && i < ORDER_BOOK_GROUPING_LIST; i++) {
    const grouping = BigNumber(marketInfo.minQuoteTick)
      .times(BigNumber(10).pow(i))
      .toFixed()
    OrderBookGroupingList.push(
      <div
        key={grouping}
        className={clsx('order-book-grouping-row', {
          'is-current': BigNumber(orderBookGrouping).eq(grouping)
        })}
        onClick={() => setOrderBookGrouping(grouping)}
      >
        {grouping}
      </div>
    )
  }

  const symbol = marketInfo?.symbol
  const dataReady = orderBookGrouping !== false
  const sizeFormat = marketInfo?.sizeDecimal || 0
  const isDefaultGrouping = BigNumber(marketInfo?.minQuoteTick).eq(
    orderBookGrouping
  )

  return (
    <div {...props}>
      <div className="card-draggable-area card-title">
        Order Book
        {dataReady && (
          <div
            ref={orderBookGroupingListRef}
            className={clsx('order-book-grouping', {
              active: orderBookGroupingListActive
            })}
            onMouseDown={e => e.stopPropagation()}
            onClick={() => setOrderBookGroupingListActive(active => !active)}
          >
            {orderBookGrouping}
            <IconChevronDown className="icon-chevron-down" />
            <div className="order-book-grouping-list">
              {OrderBookGroupingList}
            </div>
          </div>
        )}
      </div>
      <div className="card-content">
        <div className="order-book-quote-header">
          <div className="order-book-cell price-cell">Price</div>
          <div className="order-book-cell-wrap">
            <div className="data-group-wrap">
              <div className="order-book-cell size-cell">Size</div>
              <div className="order-book-cell total-cell">Total</div>
            </div>
          </div>
        </div>
        <div className="quote-list is-ask">
          {dataReady &&
            generateQuoteList({
              symbol,
              side: 'ask',
              quoteList: orderBook.asks,
              orderBookGrouping,
              isDefaultGrouping,
              sizeFormat
            })}
          <div className="empty-fill-box" />
        </div>
        {dataReady ? (
          <LastPrice showMarkPrice={true} />
        ) : (
          <div className="last-price-container" />
        )}
        <div className="quote-list">
          {dataReady &&
            generateQuoteList({
              symbol,
              side: 'bid',
              quoteList: orderBook.bids,
              orderBookGrouping,
              isDefaultGrouping,
              sizeFormat
            })}
          <div className="empty-fill-box" />
        </div>
      </div>
      {children}
    </div>
  )
})
